import Err from '@leverege/error'

import RouteUtils from './RouteUtils.js'
import { User } from './authz/index.js'
import Srv from './Srv.js'
import Rule from './Rule.js'
import Rules from './Rules.js'
import ApiAccessKey from './ApiAccessKey.js'
import ApiAccessKeys from './ApiAccessKeys.js'
import ResourceTemporaryUsers from './temporaryUser/ResourceTemporaryUsers.js'
import ResourceTemporaryUser from './temporaryUser/ResourceTemporaryUser.js'

export default function DeviceApi( api, dev, isRoot = true ) {
  
  let _path = null
  if ( dev ) {
    if ( typeof dev === 'string' ) {
      _path = `${isRoot ? 'v1/' : ''}device/${dev}`
    } else if ( dev.systemId && dev.id ) {
      _path = `${isRoot ? 'v1/' : ''}system/${dev.systemId}/device/${dev.id}`  
    } else if ( dev.id ) {
      _path = `${isRoot ? 'v1/' : ''}device/${dev.id}`
    }
  }

  if ( _path == null ) {
    throw Err( 'invalid dev' )
  }
  const connect = api.child( _path )

  /**
   * Sets the name of of the device on the specified network
   **/
  function setNetworkAlias( networkId, key, value, options ) {
    const add = [ { op : 'replace', path : `networkAliases/${networkId}/${key}`, value } ]
    return connect.patch( add, options )
  }

  function message( msg, options ) {
    return connect.child( 'sendMessage' ).post( msg, options )
  }

  /**
   * @param  {Object} options an options object
   * @param {Object} options.attributes an array of attributes objects, where attributes look like:
   *    [
   *      '<datapath>',
   *      '<alias>',
   *      <optional function object>{ function : <db function to use>, args : <array of arguments> },
   *      '<type to cast to>'
   *    ]
   *    Notes:
   *    - data paths can either reference into the json data field, or root columns
   *      root columns look like '$deviceId' ( note the $ ) and json data paths look like 'vessel/location/lat'
   *    - aliases can be anything alphanumeric
   *    - the args in the function command can reference that column, or other columns with the
   *      @COLUMN(<column alias>) syntax where if column alias is null the column is assumed to be the current
   *      attribute
   *    - if attributes doesn't exist all attributes will be fetched
   *    - the root attributes are id, time, data, userId
   * 
   * @param {Object} options.where an array of logic to query the dbs with, see eventTest and historyTest for examples
        Notes:
          there are two types of logic arrays, of length two or three, the three array is formatted like
          [ '<column/column alias>', 'operator', <value to compare> ]
          the two array if formatted like
          [ <logic operator - and/or>, <array of further tuplets or triplets> ]
   * @param {Object} options.order an array of fields to order by, can refer to custom fields from attributes
   *    Notes:
   *      The array can be of either aliases or arrays that look like [ <alias>, <ordering> ]
   * @return {Promise<Object} returns a promise that will resolve to the historical data
   */
  function history( q, query ) {
    return connect.child( 'history' ).get( { ...query, q : JSON.stringify( q ) } )
  }

  function events() {
    return {
      /**
       * @param  {Object} options see Device/history above
       *    Note: the main difference is the root paths, which are
       *          id, time, type, data, and userId
       * @param {Object} options.ack whether or not to include the ack data in the returned fields
       *                             if this is explicitly true an event must be acknowledged to be returned
       * @return {Promise<Object} returns a promise that resolves to the events
       */
      list : ( q, query ) => connect.child( 'events' ).get( { ...query, q : JSON.stringify( q ) } ),
      create : options => connect.child( 'events' ).post( options ),
      ack : ( eventId, data ) => connect.child( `events/${eventId}/ack` ).post( data )
    }
  }

  // options = { start : <string parsable by new Date()), end : <option date string>}
  function getHistorical( options ) {
    return connect.child( 'historical' ).get( options )
  }

  function verify( options ) {
    return connect.child( 'verify' ).get( options, { useRefresh : true } )
  }

  // set data directly
  function setValue( path, value, options ) {
    let body = path
    if ( typeof path === 'string' ) {
      body = { values : [ 
        { path, value }
      ] }
    }
    return connect.child( 'setValue' ).put( body, options )
  }

  // Simulation
  function startSim( options ) {
    return connect.child( 'start' ).put( null, options )
  }

  function pauseSim( options ) {
    return connect.child( 'pause' ).put( null, options )
  }

  function playScenario( scenarioId, options ) {
    const body = { scenarioId }
    return connect.child( 'scenario' ).post( body, options )
  }

  function stopScenario( scenarioId, options ) {
    const body = { scenarioId }
    return connect.child( 'scenario' ).delete( body, options )
  }

  function resetSim( options ) {
    return connect.child( 'reset' ).put( null, options )
  }

  function setUpdater( path, updater, options ) {
    return connect.child( 'updater' ).put( { path, updater }, options )
  }

  function getUpdater( path ) {
    const options = { path }
    return connect.child( 'updater' ).get( options )
  }

  function getUpdaters( options ) {
    return connect.child( 'updater' ).get( options )
  }

  function temporaryUsers() {
    return new ResourceTemporaryUsers( connect )
  }

  function temporaryUser( tId ) {
    return new ResourceTemporaryUser( connect, tId )
  }

  return {
    get : connect.get,
    update : connect.patch,
    delete : connect.delete,
    srv( type ) { return new Srv( connect, type ) },

    history,
    events,
    message,
    setValue,
    getHistorical,
    setNetworkAlias,
    startSim,
    pauseSim,
    resetSim,
    setUpdater,
    getUpdater,
    getUpdaters,
    playScenario,
    stopScenario,
    verify,
    user( id ) { return new User( connect, id ) },
    users( ) {
      return RouteUtils.authzUsers( connect, 'user' )
    },
    rules() { return new Rules( connect, false ) },
    rule( id ) { return new Rule( connect, id ) },
    apiAccessKeys() { return new ApiAccessKeys( connect.child( 'apiAccess' ) ) },
    apiAccessKey( id ) { return new ApiAccessKey( connect.child( 'apiAccess' ), id ) },
    temporaryUser,
    temporaryUsers,
  }
}
